import React from 'react';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import NumberSelector from './NumberSelector.jsx';

function getDateString(date) {
  return date.toISOString().split('T')[0];
}

function getDateArray(date) {
  return getDateString(date).split('-').map(item => Number.parseInt(item));
}

function offsetDate(offset, date) {
  const baseDate = new Date(date);
  const dateArray = getDateArray(baseDate);
  return new Date(dateArray[0], dateArray[1] + 1 + offset, dateArray[2]);
}

function getLastDay(year, month) {
  return (new Date(year, month, 0)).getDate();
}

function makeRestrictedOptions({min, max, availableMin, availableMax, step}) {
  step = step || 1;
  availableMin = availableMin || min;
  availableMax = availableMax || max;
  const options = [];
  for (let value = min; value <= max; value = value + step) {
    const disabled = (value < availableMin || value > availableMax);
    options.push({value, name: value, disabled});
  }
  return options;
}

class BoundedDate {
  constructor(...args) {
    let offset = 24;
    let dateSet;
    let dateMin;
    let dateMax;
    if (args[0] === undefined) dateSet = new Date();
    else dateSet = new Date(args[0]);
    if (args.length <= 2) offset = args[1];
    if (args.length > 2 && args[2] !== undefined && args[2] !== undefined) {
      dateMin = new Date(args[1]);
      dateMax = new Date(args[2]);
    }
    dateMin = dateMin || offsetDate(-1 * offset, dateSet);
    dateMax = dateMax || offsetDate(offset - 1, dateSet);
    let optionsForSelector;
    const privileged = {
      toString() {
        return getDateString(dateSet);
      },
      toArray() {
        return getDateArray(dateSet);
      },
      getYear() {
        return dateSet.getFullYear();
      },
      getMinYear() {
        return dateMin.getFullYear();
      },
      getMaxYear() {
        return dateMax.getFullYear();
      },
      getMonth() {
        return dateSet.getMonth() + 1;
      },
      getMinMonth(year) {
        if (year === dateMin.getFullYear()) return dateMin.getMonth() + 1;
        else return 1;
      },
      getMaxMonth(year) {
        if (year === dateMax.getFullYear()) return dateMax.getMonth() + 1;
        else return 12;
      },
      getDay() {
        return dateSet.getDate();
      },
      getMinDay(year, month) {
        if (year === dateMin.getFullYear()
        && month === (dateMin.getMonth() + 1)) return dateMin.getDate();
        else return 1;
      },
      getMaxDay(year, month) {
        if (year === dateMax.getFullYear()
        && month === (dateMax.getMonth() + 1)) return dateMax.getDate();
        else return getLastDay(year, month);
      },
      updateYear(value) {
        const minYear = this.getMinYear();
        const maxYear = this.getMaxYear();
        if (value >= maxYear) {
          dateSet.setYear(maxYear);
          const maxMonth = this.getMaxMonth(this.year);
          if (this.month >= maxMonth) {
            dateSet.setMonth(maxMonth - 1);
            const maxDay = this.getMaxDay(this.year, this.month);
            if (this.day >= maxDay) dateSet.setDate(maxDay);
          }
        } else if (value <= minYear) {
          dateSet.setYear(minYear);
          const minMonth = this.getMinMonth(this.year);
          if (this.month <= minMonth) {
            dateSet.setMonth(minMonth - 1);
            const minDay = this.getMinDay(this.year, this.month);
            if (this.day < minDay) dateSet.setDate(minDay);
          }
        } else {
          const lastDay = getLastDay(value, this.month);
          if (this.day > lastDay) dateSet.setDate(lastDay);
          dateSet.setYear(value);
        }
      },
      updateMonth(value) {
        const minMonth = this.getMinMonth(this.year);
        const maxMonth = this.getMaxMonth(this.year);
        if (value >= maxMonth) {
          dateSet.setMonth(maxMonth - 1);
          const maxDay = this.getMaxDay(this.year, this.month);
          if (this.day >= maxDay) dateSet.setDate(maxDay);
        } else if (value <= minMonth) {
          dateSet.setMonth(minMonth - 1);
          const minDay = this.getMinDay(this.year, this.month);
          if (this.day < minDay) dateSet.setDate(minDay);
        } else {
          const lastDay = getLastDay(this.year, value);
          if (this.day > lastDay) dateSet.setDate(lastDay);
          dateSet.setMonth(Number.parseInt(value - 1));
        }
      },
      updateDay(value) {
        const minDay = this.getMinDay(this.year, this.month);
        const maxDay = this.getMaxDay(this.year, this.month);
        if (this.day > maxDay) dateSet.setDate(maxDay);
        else if (this.day < minDay) dateSet.setDate(minDay);
        else dateSet.setDate(value);
      },
    };
    Object.assign(this, privileged);
  }

  get year() {
    return this.getYear();
  }

  get month() {
    return this.getMonth();
  }

  get day() {
    return this.getDay();
  }

  get optionsForSelectors() {
    const years = makeRestrictedOptions({
      min: this.getMinYear(),
      max: this.getMaxYear(),
    });
    const months = makeRestrictedOptions({
      min: 1,
      max: 12,
      availableMin: this.getMinMonth(this.year),
      availableMax: this.getMaxMonth(this.year),
    });
    const days = makeRestrictedOptions({
      min: 1,
      max: getLastDay(this.year, this.month),
      availableMin: this.getMinDay(this.year, this.month),
      availableMax: this.getMaxDay(this.year, this.month),
    });
    return {years, months, days};
  }
}

const DatePickerButton = (props) => {
  const handleClick = (ev) => {
    ev.preventDefault();
    props.onClick();
  }
  return (
    <div className="form-group">
      <label>&nbsp;</label>
        <button onClick={handleClick} className="btn btn-primary form-control">
          <i className="fa fa-calendar" aria-hidden="true"></i>
        </button>
    </div>
  )
};

export default function DateSelector(props) {
  const date = new BoundedDate(props.value, props.min, props.max);
  const options = date.optionsForSelectors;
  const handleChange = function (field) {
    if (props.onChange) props.onChange(field);
    else console.log(field.name, field.value);

  };
  const updateYear = function(field) {
    date.updateYear(field.value);
    handleChange({name: props.name, value: date.toString()});
  };
  const updateMonth = function(field) {
    date.updateMonth(field.value);
    handleChange({name: props.name, value: date.toString()});
  };
  const updateDay = function(field) {
    date.updateDay(field.value);
    handleChange({name: props.name, value: date.toString()});
  };
  const yearSelectorProps = {
    id: 'select-' + props.name + '-year',
    label: 'year',
    name: props.name + '[year]',
    value: date.year,
    options: options.years,
    onChange: updateYear,
  };
  const monthSelectorProps = {
    id: 'select-' + props.name + '-month',
    label: 'month',
    name: props.name + '[month]',
    value: date.month,
    options: options.months,
    onChange: updateMonth,
  };
  const daySelectorProps = {
    id: 'select-' + props.name + '-day',
    label: 'day',
    name: props.name + '[day]',
    value: date.day,
    options: options.days,
    onChange: updateDay,
  };
  const datePicker = props.withDatePicker ? (
    <DatePicker
      customInput={<DatePickerButton />}
      locale={props.locale || 'en-gb'}
      value={props.value ? moment(props.value) : ''}
      onChange = {(newDate) => {
        handleChange({name: props.name, value: newDate.toString()})
      }}
    />
  ) : null;
  let formGroupClassName = 'form-group';
  if (props.error) formGroupClassName += ' has-error';
  return (
    <div className={formGroupClassName}>
      <div className="col-sm-12" style={{paddingLeft: 0}}>
        <label>{props.label}</label>
      </div>
      <div className="col-sm-12">
        <div className="col-sm-3" style={{paddingLeft: 0}}>
          <NumberSelector {...yearSelectorProps} />
        </div>
        <div className="col-sm-3" style={{paddingLeft: 0}}>
          <NumberSelector {...monthSelectorProps} />
        </div>
        <div className="col-sm-3" style={{paddingLeft: 0}}>
          <NumberSelector {...daySelectorProps} />
        </div>
        <div className="col-sm-3" style={{paddingLeft: 0}}>
          {datePicker}
        </div>
      </div>
      <span
        className="help-block col-sm-12"
        style={{marginTop: '-10px', marginLeft: '-10px'}}
      >{props.error || props.message}</span>
    </div>
  );
}



// WEBPACK FOOTER //
// ../hyperdb-utils/src/components/ui-react/DateSelector.jsx